///
/// EarthBrowser
///
/// Created by Matt Giger on 10/10/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Derives engagement events from redux actions the app already dispatches,
/// so most instrumentation needs no component changes: layer on/off and
/// dwell, entity selection, time-control use, camera modes, story playback
/// (from dialog/setActiveStory transitions), artifact creation (upsert of a
/// previously unknown id), and window opens. Runs AFTER memoryGateMiddleware
/// so a blocked enable is not counted as an enable (the gate emits
/// layer_blocked_memory itself).
///
/// Design: documentation/planning/ANALYTICS_STRATEGY.md §4.2
///

import type { Middleware } from "@reduxjs/toolkit"
import type { AnalyticsEventProps } from "@shared-types"
import { noteLayerDisabled, noteLayerEnabled, noteUiInteraction, noteWindowOpened, recentInput, secondsSinceLoad, track } from "@/utils/analytics"
import { openDatasetWindow, setActiveStory, setCameraMode } from "./dialogSlice"
import {
	sendCameraCommand,
	sendDatasetAction,
	setClockAnimatingSpeed,
	setClockCurrentTime,
	setClockEndOffset,
	setClockStartOffset,
	setClockState,
	setTimeControlExpanded,
	toggleTimeControlExpanded,
} from "./earthBrowserSlice"
import { upsertPlacemark } from "./placemarksSlice"
import { upsertView } from "./savedViewsSlice"
import { addStop, upsertStory } from "./storiesSlice"

type TimeMode = AnalyticsEventProps["time_control_used"]["mode"]

interface ActiveStory {
	storyId: string
	stopIndex: number
	guided: boolean
}

// Minimal view of the state slices this middleware reads (avoids importing
// RootState, which would create a store ↔ middleware import cycle)
interface SliceView {
	dialog?: { activeStory: ActiveStory | null }
	savedViews?: { views: Record<string, unknown> }
	placemarks?: { placemarks: Record<string, unknown> }
	stories?: { stories: Record<string, { stops?: unknown[] }> }
}

/** time_control_used fires once per mode per session (it is a share metric) */
const timeModesSeen = new Set<TimeMode>()
function noteTimeMode(mode: TimeMode): void {
	if (timeModesSeen.has(mode)) return
	timeModesSeen.add(mode)
	track("time_control_used", { mode })
}

// Story playback bookkeeping: setActiveStory carries (storyId, stopIndex);
// transitions from/to null bracket a play, index changes are stop views
let storyStartedAt = 0
let stopEnteredAt = 0

function stopsOf(state: SliceView, storyId: string): number {
	return state.stories?.stories[storyId]?.stops?.length ?? 0
}

function onStoryTransition(prev: ActiveStory | null, next: ActiveStory | null, state: SliceView): void {
	const now = performance.now()
	if (!prev && next) {
		storyStartedAt = now
		stopEnteredAt = now
		track("story_played", { stops: stopsOf(state, next.storyId), guided: next.guided })
		return
	}
	if (prev && next?.storyId === prev.storyId && next.stopIndex !== prev.stopIndex) {
		track("story_stop_viewed", { index: prev.stopIndex, dwell_s: Math.round((now - stopEnteredAt) / 1000) })
		stopEnteredAt = now
		return
	}
	if (prev && !next) {
		const stops = stopsOf(state, prev.storyId)
		track("story_stop_viewed", { index: prev.stopIndex, dwell_s: Math.round((now - stopEnteredAt) / 1000) })
		if (stops > 0 && prev.stopIndex >= stops - 1) {
			track("story_completed", { stops, total_s: Math.round((now - storyStartedAt) / 1000) })
		}
	}
}

export const analyticsMiddleware: Middleware = (api) => (next) => (action) => {
	const before = api.getState() as SliceView

	// ---- events that need pre-state (creation detection, story transitions) ----
	if (upsertView.match(action) && !before.savedViews?.views[action.payload.id]) {
		track("artifact_created", { kind: "view" })
	} else if (upsertPlacemark.match(action) && !before.placemarks?.placemarks[action.payload.id]) {
		track("artifact_created", { kind: "placemark" })
	} else if (upsertStory.match(action) && !before.stories?.stories[action.payload.id]) {
		track("artifact_created", { kind: "story" })
	}

	const result = next(action)

	try {
		if (sendDatasetAction.match(action)) {
			const { type, action: kind } = action.payload
			if (!type) return result
			if (kind === "enable") {
				noteLayerEnabled(type)
				track("layer_enabled", {
					layer: type,
					since_load_s: secondsSinceLoad(),
					source: recentInput(5000) ? "user" : "launch",
				})
			} else if (kind === "disable") {
				track("layer_disabled", { layer: type, dwell_s: noteLayerDisabled(type) })
			} else if (kind === "selectId" || kind === "selectCoords") {
				noteUiInteraction()
				track("entity_selected", { layer: type })
			}
		// Clock actions are also dispatched by the engine (real-time ticks,
		// rehydration), so only input-adjacent ones count as time-control use
		} else if (setClockState.match(action)) {
			if (recentInput()) noteTimeMode(action.payload === "animating" ? "play" : action.payload === "paused" ? "pause" : "realtime")
		} else if (setClockAnimatingSpeed.match(action)) {
			if (recentInput()) noteTimeMode("speed")
		} else if (setClockCurrentTime.match(action)) {
			if (recentInput()) noteTimeMode("scrub")
		} else if (setClockStartOffset.match(action) || setClockEndOffset.match(action)) {
			if (recentInput()) noteTimeMode("range")
		} else if ((setTimeControlExpanded.match(action) && action.payload) || toggleTimeControlExpanded.match(action)) {
			if (recentInput()) noteTimeMode("expand")
		} else if (setCameraMode.match(action)) {
			track("camera_mode_changed", { mode: action.payload })
		} else if (sendCameraCommand.match(action)) {
			noteUiInteraction()
			if (action.payload.command === "view/flyTo") {
				track("view_recalled", {})
			}
		} else if (setActiveStory.match(action)) {
			onStoryTransition(before.dialog?.activeStory ?? null, action.payload, api.getState() as SliceView)
		} else if (openDatasetWindow.match(action)) {
			noteWindowOpened()
			track("window_opened", { window_type: action.payload.windowType ?? "unknown" })
		} else if (addStop.match(action)) {
			// Authoring progress: a stop added to an existing story
			noteUiInteraction()
		}
	} catch { /* analytics never breaks the action pipeline */ }

	return result
}
